import type { Bedrooms, Lang, Localized } from '../types/funnel';

/**
 * Visual inventory catalog. Every quantity starts at ZERO; nothing is added unless the customer taps it.
 * Items with variants are stored under `${itemId}:${variantId}` so each variant keeps its own quantity.
 * `units` are internal planning units only — not weight, cubic feet or price.
 */
export interface ItemVariant {
  id: string;
  label: Localized;
  units: number;
  /** Variant needs specific handling review (e.g. glass/stone). Does not block the move. */
  review?: boolean;
}

export interface InventoryItem {
  id: string;
  label: Localized;
  units: number;
  variants?: ItemVariant[];
  /** Short guidance shown on the card. */
  note?: Localized;
  /** Extra search words (both languages). */
  keywords?: string;
}

export interface InventoryRoom {
  id: string;
  label: Localized;
  items: InventoryItem[];
}

export interface SpecialItem {
  id: string;
  label: Localized;
  requiresReview: boolean;
}

const r1 = (n: number) => Math.round(n * 10) / 10;

function bedVariants(u: number): ItemVariant[] {
  return [
    { id: 'standard', label: { en: 'Frame + mattress', es: 'Base + colchón' }, units: u },
    { id: 'platform', label: { en: 'Platform + mattress', es: 'Plataforma + colchón' }, units: u },
    { id: 'storage', label: { en: 'Storage bed + mattress', es: 'Cama con cajones + colchón' }, units: r1(u + 0.5) },
    { id: 'adjustable', label: { en: 'Adjustable base + mattress', es: 'Base ajustable + colchón' }, units: r1(u + 0.5) },
    { id: 'mattress', label: { en: 'Mattress only', es: 'Solo colchón' }, units: r1(u * 0.4) },
    { id: 'frame', label: { en: 'Frame only', es: 'Solo base' }, units: r1(u * 0.6) },
  ];
}

const bedNote: Localized = {
  en: 'A complete bed already includes its mattress — don’t add the mattress again.',
  es: 'Una cama completa ya incluye su colchón; no lo agregue otra vez.',
};

export const inventoryRooms: InventoryRoom[] = [
  {
    id: 'bedroom',
    label: { en: 'Bedrooms', es: 'Recámaras' },
    items: [
      { id: 'bed_twin', label: { en: 'Twin bed', es: 'Cama twin' }, units: 1.5, variants: bedVariants(1.5), note: bedNote, keywords: 'cama individual sencilla' },
      { id: 'bed_full', label: { en: 'Full bed', es: 'Cama full' }, units: 2, variants: bedVariants(2), note: bedNote, keywords: 'matrimonial double' },
      { id: 'bed_queen', label: { en: 'Queen bed', es: 'Cama queen' }, units: 2.5, variants: bedVariants(2.5), note: bedNote },
      { id: 'bed_king', label: { en: 'King bed', es: 'Cama king' }, units: 3, variants: bedVariants(3), note: bedNote },
      { id: 'bed_cal_king', label: { en: 'California King bed', es: 'Cama California King' }, units: 3, variants: bedVariants(3), note: bedNote, keywords: 'cal king' },
      {
        id: 'bunk_bed',
        label: { en: 'Bunk bed', es: 'Litera' },
        units: 3,
        variants: [
          { id: 'twin_twin', label: { en: 'Twin over twin', es: 'Twin sobre twin' }, units: 3 },
          { id: 'twin_full', label: { en: 'Twin over full', es: 'Twin sobre full' }, units: 3.5 },
        ],
        note: { en: 'Includes mattresses. Disassembly is a separate service.', es: 'Incluye colchones. El desarme es un servicio aparte.' },
        keywords: 'litera camarote',
      },
      {
        id: 'dresser',
        label: { en: 'Dresser', es: 'Cómoda' },
        units: 2.5,
        variants: [
          { id: 'low_wide', label: { en: 'Low / wide', es: 'Baja / ancha' }, units: 2.5 },
          { id: 'tall_chest', label: { en: 'Tall chest', es: 'Cajonera alta' }, units: 2 },
          { id: 'double', label: { en: 'Double dresser', es: 'Cómoda doble' }, units: 3 },
          { id: 'mirror', label: { en: 'With detachable mirror', es: 'Con espejo desmontable' }, units: 3 },
        ],
        keywords: 'gavetero cajonera chest',
      },
      {
        id: 'nightstand',
        label: { en: 'Nightstand', es: 'Buró / mesa de noche' },
        units: 0.5,
        variants: [
          { id: 'small', label: { en: 'Small', es: 'Pequeño' }, units: 0.4 },
          { id: 'standard', label: { en: 'Standard', es: 'Estándar' }, units: 0.5 },
          { id: 'oversized', label: { en: 'Oversized', es: 'Grande' }, units: 0.8 },
          { id: 'glass_stone', label: { en: 'Glass or stone top', es: 'Cubierta de vidrio o piedra' }, units: 0.8, review: true },
        ],
      },
      { id: 'wardrobe', label: { en: 'Wardrobe / armoire', es: 'Ropero / armario' }, units: 3 },
      {
        id: 'mirror',
        label: { en: 'Mirror', es: 'Espejo' },
        units: 0.5,
        variants: [
          { id: 'wall', label: { en: 'Wall mirror', es: 'Espejo de pared' }, units: 0.4 },
          { id: 'floor', label: { en: 'Floor mirror', es: 'Espejo de piso' }, units: 0.6 },
        ],
      },
    ],
  },
  {
    id: 'living',
    label: { en: 'Living room', es: 'Sala' },
    items: [
      { id: 'sofa', label: { en: 'Sofa', es: 'Sofá' }, units: 3, variants: [
        { id: 'standard', label: { en: '3-seat', es: '3 plazas' }, units: 3 },
        { id: 'sleeper', label: { en: 'Sleeper sofa', es: 'Sofá cama' }, units: 4 },
      ] },
      { id: 'loveseat', label: { en: 'Loveseat', es: 'Loveseat' }, units: 2, keywords: 'sofa 2 plazas' },
      { id: 'sectional', label: { en: 'Sectional', es: 'Seccional' }, units: 5, variants: [
        { id: 'p2', label: { en: '2 pieces', es: '2 piezas' }, units: 3.5 },
        { id: 'p3', label: { en: '3 pieces', es: '3 piezas' }, units: 5 },
        { id: 'p4', label: { en: '4 pieces', es: '4 piezas' }, units: 6.5 },
        { id: 'p5', label: { en: '5+ pieces', es: '5+ piezas' }, units: 8 },
      ] },
      { id: 'armchair', label: { en: 'Armchair / recliner', es: 'Sillón / reclinable' }, units: 1.5, variants: [
        { id: 'armchair', label: { en: 'Armchair', es: 'Sillón' }, units: 1.5 },
        { id: 'recliner', label: { en: 'Recliner', es: 'Reclinable' }, units: 2 },
      ] },
      { id: 'coffee_table', label: { en: 'Coffee table', es: 'Mesa de centro' }, units: 1, variants: [
        { id: 'wood', label: { en: 'Wood / metal', es: 'Madera / metal' }, units: 1 },
        { id: 'glass_stone', label: { en: 'Glass or stone top', es: 'Vidrio o piedra' }, units: 1.2, review: true },
      ] },
      { id: 'side_table', label: { en: 'Side / end table', es: 'Mesa auxiliar' }, units: 0.5 },
      { id: 'tv', label: { en: 'TV', es: 'Televisión' }, units: 1, variants: [
        { id: 's32', label: { en: 'Up to 32"', es: 'Hasta 32"' }, units: 0.4 },
        { id: 's40', label: { en: '40"–43"', es: '40"–43"' }, units: 0.6 },
        { id: 's50', label: { en: '50"', es: '50"' }, units: 0.8 },
        { id: 's55', label: { en: '55"', es: '55"' }, units: 1 },
        { id: 's65', label: { en: '65"', es: '65"' }, units: 1.2 },
        { id: 's75', label: { en: '75"', es: '75"' }, units: 1.5 },
        { id: 's85', label: { en: '85" or larger', es: '85" o más' }, units: 2, review: true },
      ], note: { en: 'Know the exact size? Add it in the item note.', es: '¿Sabe el tamaño exacto? Escríbalo en la nota del artículo.' }, keywords: 'television tele pantalla' },
      { id: 'tv_stand', label: { en: 'TV stand', es: 'Mueble de TV' }, units: 1.5 },
      { id: 'bookcase', label: { en: 'Bookcase', es: 'Librero' }, units: 1.5, variants: [
        { id: 'short', label: { en: 'Short', es: 'Bajo' }, units: 1 },
        { id: 'tall', label: { en: 'Tall', es: 'Alto' }, units: 1.5 },
      ] },
    ],
  },
  {
    id: 'kitchen',
    label: { en: 'Kitchen & dining', es: 'Cocina y comedor' },
    items: [
      { id: 'dining_table', label: { en: 'Dining table', es: 'Mesa de comedor' }, units: 2, variants: [
        { id: 'seat4', label: { en: 'Seats 4', es: '4 personas' }, units: 1.5 },
        { id: 'seat6', label: { en: 'Seats 6', es: '6 personas' }, units: 2 },
        { id: 'seat8', label: { en: 'Seats 8+', es: '8+ personas' }, units: 3 },
        { id: 'glass_stone', label: { en: 'Glass or stone top', es: 'Vidrio o piedra' }, units: 2.5, review: true },
      ] },
      { id: 'dining_chair', label: { en: 'Dining chair', es: 'Silla de comedor' }, units: 0.5 },
      { id: 'bar_stool', label: { en: 'Bar stool', es: 'Banco alto' }, units: 0.4 },
      { id: 'china_cabinet', label: { en: 'China cabinet / hutch', es: 'Vitrina / trinchador' }, units: 3 },
      { id: 'fridge', label: { en: 'Refrigerator', es: 'Refrigerador' }, units: 4, variants: [
        { id: 'top', label: { en: 'Top freezer', es: 'Congelador arriba' }, units: 3.5 },
        { id: 'french', label: { en: 'French door', es: 'Puerta francesa' }, units: 4.5 },
        { id: 'side', label: { en: 'Side-by-side', es: 'Dos puertas laterales' }, units: 4.5 },
        { id: 'mini', label: { en: 'Mini fridge', es: 'Minirefrigerador' }, units: 1 },
      ], note: { en: 'Tell us if it’s still connected — our team confirms what the crew can handle.', es: 'Díganos si sigue conectado; nuestro equipo confirma lo que puede hacer la cuadrilla.' } },
      { id: 'washer', label: { en: 'Washer', es: 'Lavadora' }, units: 3, note: { en: 'Tell us if it’s still connected.', es: 'Díganos si sigue conectada.' } },
      { id: 'dryer', label: { en: 'Dryer', es: 'Secadora' }, units: 3, note: { en: 'Gas or electric? Add it in the item note.', es: '¿De gas o eléctrica? Escríbalo en la nota.' } },
    ],
  },
  {
    id: 'office',
    label: { en: 'Office', es: 'Oficina' },
    items: [
      { id: 'desk', label: { en: 'Desk', es: 'Escritorio' }, units: 2, variants: [
        { id: 'standard', label: { en: 'Standard', es: 'Estándar' }, units: 2 },
        { id: 'l_shaped', label: { en: 'L-shaped', es: 'En forma de L' }, units: 3 },
        { id: 'standing', label: { en: 'Standing desk', es: 'Escritorio de pie' }, units: 2.5 },
      ] },
      { id: 'office_chair', label: { en: 'Office chair', es: 'Silla de oficina' }, units: 0.5 },
      { id: 'monitor', label: { en: 'Monitor', es: 'Monitor' }, units: 0.3 },
      { id: 'file_cabinet', label: { en: 'Filing cabinet', es: 'Archivero' }, units: 1.5, variants: [
        { id: 'd2', label: { en: '2 drawers', es: '2 cajones' }, units: 1 },
        { id: 'd3', label: { en: '3 drawers', es: '3 cajones' }, units: 1.5 },
        { id: 'd4', label: { en: '4 drawers', es: '4 cajones' }, units: 2 },
      ] },
    ],
  },
  {
    id: 'outdoor',
    label: { en: 'Garage & patio', es: 'Garaje y patio' },
    items: [
      { id: 'shelving', label: { en: 'Garage shelving', es: 'Estante de garaje' }, units: 1.5 },
      { id: 'tool_chest', label: { en: 'Tool chest', es: 'Caja de herramientas' }, units: 2 },
      { id: 'grill', label: { en: 'Grill', es: 'Asador' }, units: 1.5, note: { en: 'Propane tanks can’t ride in the truck — tell us if you have one.', es: 'Díganos si tiene tanque de propano.' } },
      { id: 'patio_set', label: { en: 'Patio set', es: 'Juego de patio' }, units: 2 },
      { id: 'bike', label: { en: 'Bicycle', es: 'Bicicleta' }, units: 0.5 },
      { id: 'storage_bin', label: { en: 'Storage bin / tote', es: 'Contenedor plástico' }, units: 0.25 },
    ],
  },
  {
    id: 'boxes',
    label: { en: 'Boxes you have', es: 'Cajas que tiene' },
    items: [
      { id: 'box_small', label: { en: 'Small box', es: 'Caja chica' }, units: 0.15 },
      { id: 'box_medium', label: { en: 'Medium box', es: 'Caja mediana' }, units: 0.2 },
      { id: 'box_large', label: { en: 'Large box', es: 'Caja grande' }, units: 0.3 },
      { id: 'box_wardrobe', label: { en: 'Wardrobe box', es: 'Caja de ropero' }, units: 0.5 },
    ],
  },
];

/** Proposed only — shown as suggestions the customer chooses. Never added silently. Keys are full inventory keys. */
export const suggestedInventory: Record<Bedrooms, Record<string, number>> = {
  few: {},
  studio: { 'bed_full:standard': 1, 'dresser:low_wide': 1, 'sofa:standard': 1, 'tv:s50': 1, box_medium: 10 },
  '1': { 'bed_queen:standard': 1, 'dresser:low_wide': 1, 'nightstand:standard': 2, 'sofa:standard': 1, 'coffee_table:wood': 1, 'tv:s55': 1, tv_stand: 1, 'dining_table:seat4': 1, dining_chair: 4, box_medium: 15 },
  '2': { 'bed_queen:standard': 1, 'bed_full:standard': 1, 'dresser:low_wide': 2, 'nightstand:standard': 3, 'sofa:standard': 1, 'armchair:armchair': 1, 'coffee_table:wood': 1, 'tv:s55': 2, tv_stand: 1, 'dining_table:seat4': 1, dining_chair: 4, 'desk:standard': 1, box_medium: 25, box_large: 8 },
  '3': { 'bed_king:standard': 1, 'bed_queen:standard': 1, 'bed_twin:standard': 1, 'dresser:low_wide': 3, 'nightstand:standard': 4, 'sectional:p3': 1, 'coffee_table:wood': 1, 'tv:s55': 3, tv_stand: 2, 'dining_table:seat6': 1, dining_chair: 6, washer: 1, dryer: 1, 'desk:standard': 1, box_medium: 35, box_large: 12 },
  '4': { 'bed_king:standard': 1, 'bed_queen:standard': 2, 'bed_twin:standard': 1, 'dresser:low_wide': 4, 'nightstand:standard': 6, wardrobe: 1, 'sofa:standard': 2, 'armchair:armchair': 2, 'coffee_table:wood': 1, 'tv:s55': 4, tv_stand: 2, 'bookcase:tall': 2, 'dining_table:seat6': 1, dining_chair: 6, 'fridge:french': 1, washer: 1, dryer: 1, 'desk:standard': 1, box_medium: 45, box_large: 15, patio_set: 1 },
  '5': { 'bed_king:standard': 2, 'bed_queen:standard': 2, 'bed_twin:standard': 1, 'dresser:low_wide': 5, 'nightstand:standard': 8, wardrobe: 2, 'sofa:standard': 2, 'sectional:p3': 1, 'armchair:armchair': 2, 'tv:s55': 5, tv_stand: 3, 'bookcase:tall': 3, 'dining_table:seat8': 1, dining_chair: 8, 'fridge:french': 2, washer: 1, dryer: 1, 'desk:standard': 2, box_medium: 60, box_large: 20, patio_set: 1, grill: 1 },
};

export const specialItems: SpecialItem[] = [
  { id: 'piano', label: { en: 'Piano', es: 'Piano' }, requiresReview: true },
  { id: 'safe', label: { en: 'Safe', es: 'Caja fuerte' }, requiresReview: true },
  { id: 'marble_glass', label: { en: 'Large marble, stone or glass piece', es: 'Pieza grande de mármol, piedra o vidrio' }, requiresReview: true },
  { id: 'heavy', label: { en: 'Unusually heavy piece', es: 'Pieza inusualmente pesada' }, requiresReview: true },
  { id: 'oversized', label: { en: 'Oversized furniture (over 8 ft)', es: 'Mueble extra grande (más de 8 pies)' }, requiresReview: true },
  { id: 'art_mirror', label: { en: 'Artwork or large mirror', es: 'Arte o espejo grande' }, requiresReview: false },
];

export const allInventoryItems = inventoryRooms.flatMap((r) => r.items);
const itemById: Record<string, InventoryItem> = Object.fromEntries(allInventoryItems.map((i) => [i.id, i]));

export function inventoryKey(itemId: string, variantId?: string): string {
  return variantId ? `${itemId}:${variantId}` : itemId;
}

export function parseInventoryKey(key: string): { item: InventoryItem | undefined; variant: ItemVariant | undefined } {
  const [itemId, variantId] = key.split(':');
  const item = itemById[itemId];
  return { item, variant: variantId ? item?.variants?.find((v) => v.id === variantId) : undefined };
}

export function unitsForKey(key: string): number {
  const { item, variant } = parseInventoryKey(key);
  return variant?.units ?? (item && !item.variants ? item.units : 0);
}

export function labelForKey(key: string, lang: Lang): string {
  const { item, variant } = parseInventoryKey(key);
  if (!item) return key;
  return variant ? `${item.label[lang]} — ${variant.label[lang]}` : item.label[lang];
}

export function keyNeedsReview(key: string): boolean {
  return !!parseInventoryKey(key).variant?.review;
}

/** Total quantity across all variants of one item. */
export function itemTotal(inv: Record<string, number>, itemId: string): number {
  return Object.entries(inv).reduce((s, [k, q]) => (k === itemId || k.startsWith(`${itemId}:`) ? s + q : s), 0);
}

/** Migrates drafts saved before variants existed: a bare id on an item with variants moves to its first variant. */
export function normalizeInventory(inv: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, q] of Object.entries(inv)) {
    if (!(q > 0)) continue;
    if (k === 'mattress') {
      out['bed_queen:mattress'] = (out['bed_queen:mattress'] ?? 0) + q;
      continue;
    }
    const item = itemById[k.split(':')[0]];
    if (!item) continue;
    const key = !k.includes(':') && item.variants ? inventoryKey(item.id, item.variants[0].id) : k;
    out[key] = (out[key] ?? 0) + q;
  }
  return out;
}
